import "@/App.css";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageSquare, Workflow, LineChart, ArrowRight, Menu, X, Check, Shield, Users } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero Background Video - Blurred video with white overlay
const HeroBackground = () => {
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
      {/* Video layer with heavy blur */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ 
          filter: 'blur(25px)',
          transform: 'scale(1.15)', // Scale up to avoid blur edge artifacts
        }}
      >
        <source 
          src="https://customer-assets.emergentagent.com/job_budget-ai-preview/artifacts/z4ppk9ye_Prova_2.mp4" 
          type="video/mp4" 
        />
      </video>
      
      {/* White semi-transparent overlay */}
      <div 
        className="absolute inset-0 bg-white/70"
        style={{ backdropFilter: 'blur(5px)' }}
      />
    </div>
  );
};

// Navigation items
const navItems = [
  { id: "a-chi-e-rivolto", label: "A chi è rivolto" },
  { id: "problema", label: "Come funziona" },
  { id: "elementi-differenzianti", label: "Vantaggi" },
  { id: "accedi", label: "Accedi in anteprima" },
];

// CTA Button Component
const CTAButton = ({ onClick, className = "" }) => (
  <button
    data-testid="cta-button"
    onClick={onClick}
    className={`group bg-black text-white hover:bg-neutral-800 rounded-full px-8 py-4 flex items-center justify-center gap-3 transition-all duration-300 active:scale-95 font-semibold text-sm tracking-wide cta-glow ${className}`}
  >
    Accedi alla BETA in anteprima, è gratis
    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
  </button>
);

// Header Component
const Header = ({ scrollToSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header 
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <button
            data-testid="logo-button"
            onClick={() => scrollToSection("home")}
            className="hover:opacity-70 transition-opacity"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_budget-ai-preview/artifacts/aema2oxx_Extended%20Color.png"
              alt="IANUS Logo"
              className="h-16 sm:h-20 w-auto bg-white"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.slice(0, -1).map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium text-neutral-600 hover:text-black transition-colors"
              >
                {item.label}
              </button>
            ))}
            {/* CTA button in header - subtle */}
            <button
              data-testid="nav-accedi"
              onClick={() => scrollToSection("accedi")}
              className="text-sm font-medium text-neutral-700 bg-neutral-200/60 hover:bg-neutral-300/70 px-4 py-2 rounded-full transition-colors"
            >
              Accedi in anteprima
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-black"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-black/5">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  scrollToSection(item.id);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-sm font-medium text-neutral-600 hover:text-black transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ scrollToSection }) => (
  <section
    id="home"
    data-testid="section-home"
    className="relative min-h-screen flex flex-col items-center justify-center pt-20 bg-white overflow-hidden"
  >
    {/* Animated Background - Chat to Charts */}
    <HeroBackground />
    
    <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32 flex-1 flex flex-col items-center justify-center">
      <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
        <h1 
          className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-tight text-black mb-8"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          IANUS, il primo Budget Manager Conversazionale al mondo
        </h1>
        
        <p className="text-lg sm:text-xl text-neutral-600 leading-relaxed mb-12 max-w-2xl">
          Il primo assistente digitale che ti permette di parlare coi numeri della tua impresa. Costruisci budget e forecast in pochi minuti dialogando con il CBM come se fosse un tuo collaboratore.
        </p>

        <CTAButton onClick={() => scrollToSection("accedi")} />
      </div>
    </div>

    {/* Scroll indicator */}
    <button 
      onClick={() => scrollToSection("a-chi-e-rivolto")}
      className="relative z-10 mb-8 flex flex-col items-center gap-2 text-black hover:text-white transition-colors cursor-pointer animate-bounce-slow"
      data-testid="scroll-indicator"
    >
      <span className="text-xs font-medium uppercase tracking-wider">Scopri di più</span>
      <svg 
        className="w-6 h-6" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </button>
  </section>
);

// Target Audience Section
const TargetSection = ({ scrollToSection }) => (
  <section
    id="a-chi-e-rivolto"
    data-testid="section-target"
    className="py-24 sm:py-32 lg:py-40 bg-neutral-50"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div>
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-6">
            A CHI È RIVOLTO
          </span>
          
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-black mb-8"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Pensato per chi guida l'azienda
          </h2>
          
          <p className="text-lg text-neutral-600 leading-relaxed mb-8">
            IANUS è progettato per Founder, CEO, COO e per tutte quelle figure operative 
            che si occupano della gestione aziendale e che necessitano di una soluzione smart e AI powered 
            per la redazione del budget dell'Impresa.
          </p>
          
          <div className="space-y-4 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-neutral-700" />
              </div>
              <p className="text-neutral-600">
                Gestisci consapevolmente la tua azienda nel presente
              </p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-neutral-700" />
              </div>
              <p className="text-neutral-600">
                Pianifica in anticipo le attività per gli sviluppi futuri
              </p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-neutral-700" />
              </div>
              <p className="text-neutral-600">
                Migliora le performance della tua impresa
              </p>
            </div>
          </div>

          <CTAButton onClick={() => scrollToSection("accedi")} />
        </div>

        <div className="relative">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-neutral-200">
            <img
              src="https://customer-assets.emergentagent.com/job_budget-ai-preview/artifacts/oxqvmhvr_Immagine.png"
              alt="Businessman using smartphone in modern business district"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Problem Section
const ProblemSection = ({ scrollToSection }) => {
  const problems = [
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Dialoga in linguaggio naturale",
      description: "Racconta costi, ricavi e obiettivi a IANUS come faresti con un consulente. Non servono competenze contabili."
    },
    {
      icon: <Workflow className="w-6 h-6" />,
      title: "Ottieni il tuo budget automatico",
      description: "IANUS traduce le risposte in scritture contabili chiare. Preciso, professionale, subito utilizzabile per la gestione aziendale."
    },
    {
      icon: <LineChart className="w-6 h-6" />,
      title: "Prendi decisioni con sicurezza",
      description: "Visualizza budget, scenari, cash flow in tempo reale. Col supporto dell'evidenza dei numeri scegli la strategia migliore."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Previeni eventuali problemi",
      description: "Intervieni tempestivamente: gli strumenti ordinari permettono solo analisi ex post, IANUS anticipa il manifestarsi di qualsiasi criticità."
    }
  ];

  return (
    <section
      id="problema"
      data-testid="section-problem"
      className="py-24 sm:py-32 lg:py-40 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-5xl mx-auto mb-16 sm:mb-24">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-6">
            COME FUNZIONA
          </span>
          
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-black mb-8"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Da numeri complessi a decisioni semplici.
          </h2>
          
          <p className="text-lg text-neutral-600 leading-relaxed">
            Il controllo di gestione tradizionale è spesso lento, frammentato ed incomprensibile per figure 
            senza competenze specifiche in materia amministrativa e contabile, IANUS riallinea la tua strategia 
            aziendale ai dati finanziari nel presente e ti permette di anticipare i problemi del futuro.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-8">
          {problems.map((problem, index) => (
            <div
              key={index}
              className="bg-neutral-50 border border-black/5 rounded-3xl p-8 sm:p-10 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-12 h-12 rounded-2xl bg-neutral-100 flex items-center justify-center text-neutral-700 mb-6">
                {problem.icon}
              </div>
              <h3 
                className="text-xl sm:text-2xl font-medium tracking-tight text-black mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {problem.title}
              </h3>
              <p className="text-neutral-600 leading-relaxed">
                {problem.description}
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-16">
          <CTAButton onClick={() => scrollToSection("accedi")} />
        </div>
      </div>
    </section>
  );
};

// Differentiators Section with static infinite loop carousel
const DifferentiatorsSection = ({ scrollToSection }) => {
  const trackRef = useRef(null);
  const positionRef = useRef(0);
  const [, forceUpdate] = useState(0);
  const scrollIntervalRef = useRef(null);
  const isTransitioningRef = useRef(false);
  
  const features = [
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Conversazionale, davvero",
      description: "È IANUS a parlare la tua lingua e non tu quella dei numeri. Fai domande naturali come faresti con il tuo CFO: \"Qual è il nostro burn rate questo mese?\""
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Affidabile e sicuro",
      description: "IANUS si basa su un impianto contabile certificato. Innovativo nella forma, sicuro e consolidato nella sostanza."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Pensato per le imprese",
      description: "IANUS tratta una materia tecnicamente complessa ma l'intera esperienza di utilizzo si basa sull'idea che sia uno strumento che serva alle aziende."
    },
    {
      icon: <LineChart className="w-8 h-8" />,
      title: "Numeri accessibili a tutti",
      description: "AI Agent conversazionale che permette la piena comprensione dei numeri della tua Impresa, senza bisogno di competenze tecniche."
    },
    {
      icon: <Workflow className="w-8 h-8" />,
      title: "Addio fogli elettronici",
      description: "Permette di sostituire strumenti datati, non ottimizzati ed inefficienti, come fogli elettronici o tool arcaici che rallentano le tue decisioni."
    }
  ];

  // Triplicate for seamless infinite loop (before + main + after)
  const triplicatedFeatures = [...features, ...features, ...features];

  // Get card dimensions
  const getCardDimensions = () => {
    const isMobile = window.innerWidth < 640;
    const cardWidth = isMobile ? 256 : 320;
    const gap = isMobile ? 16 : 24;
    return { cardWidth, gap, singleSetWidth: features.length * (cardWidth + gap) };
  };

  // Initialize position to middle set
  useEffect(() => {
    const { singleSetWidth } = getCardDimensions();
    positionRef.current = -singleSetWidth;
    forceUpdate(n => n + 1);
  }, []);

  // Scroll function with seamless infinite loop
  const scroll = (direction) => {
    if (isTransitioningRef.current) return;
    
    const { cardWidth, gap, singleSetWidth } = getCardDimensions();
    const scrollAmount = (cardWidth + gap) / 2; // Half a card
    
    isTransitioningRef.current = true;
    positionRef.current += direction * scrollAmount;
    forceUpdate(n => n + 1);
    
    // After transition, check if we need to reset position for infinite loop
    setTimeout(() => {
      const { singleSetWidth } = getCardDimensions();
      
      // If scrolled too far right (showing before-set), jump to main set
      if (positionRef.current > -singleSetWidth * 0.2) {
        positionRef.current -= singleSetWidth;
        forceUpdate(n => n + 1);
      }
      // If scrolled too far left (showing after-set), jump to main set
      else if (positionRef.current < -singleSetWidth * 1.8) {
        positionRef.current += singleSetWidth;
        forceUpdate(n => n + 1);
      }
      
      isTransitioningRef.current = false;
    }, 300);
  };

  // Handle arrow click
  const handleLeftClick = () => scroll(1);
  const handleRightClick = () => scroll(-1);

  // Handle arrow hold
  const startScrolling = (direction) => {
    scroll(direction);
    scrollIntervalRef.current = setInterval(() => scroll(direction), 350);
  };

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  return (
    <section
      id="elementi-differenzianti"
      data-testid="section-differentiators"
      className="py-24 sm:py-32 lg:py-40 bg-neutral-50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-24">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-6">
            VANTAGGI
          </span>
          
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-black mb-8"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            L'intelligenza che fa la differenza.
          </h2>
        </div>

        {/* Carousel container with arrows outside */}
        <div className="flex items-center gap-4">
          {/* Left arrow */}
          <button 
            onClick={handleLeftClick}
            onMouseDown={() => startScrolling(1)}
            onMouseUp={stopScrolling}
            onMouseLeave={stopScrolling}
            onTouchStart={() => startScrolling(1)}
            onTouchEnd={stopScrolling}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
            aria-label="Scorri a sinistra"
            data-testid="carousel-arrow-left"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Carousel with fade */}
          <div className="relative flex-1 overflow-hidden">
            {/* Fade overlay left */}
            <div className="absolute left-0 top-0 bottom-0 w-6 sm:w-16 bg-gradient-to-r from-neutral-50 to-transparent z-10 pointer-events-none" />
            
            {/* Fade overlay right */}
            <div className="absolute right-0 top-0 bottom-0 w-6 sm:w-16 bg-gradient-to-l from-neutral-50 to-transparent z-10 pointer-events-none" />
            
            {/* Static track with smooth transition - infinite loop */}
            <div 
              ref={trackRef}
              className="flex gap-4 sm:gap-6"
              style={{ 
                width: 'fit-content',
                transform: `translateX(${positionRef.current}px)`,
                transition: isTransitioningRef.current ? 'transform 0.3s ease-out' : 'none'
              }}
              data-testid="carousel-track"
            >
              {triplicatedFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-64 sm:w-80 bg-white border border-black/5 rounded-3xl p-6 sm:p-8 shadow-sm text-center cursor-default transition-shadow hover:shadow-md"
                  style={{ minHeight: '280px' }}
                  data-testid={`carousel-card-${index}`}
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-neutral-100 flex items-center justify-center text-neutral-700 mx-auto mb-4 sm:mb-5">
                    {feature.icon}
                  </div>
                  <h3 
                    className="text-base sm:text-xl font-medium tracking-tight text-black mb-2 sm:mb-3"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed text-xs sm:text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right arrow */}
          <button 
            onClick={handleRightClick}
            onMouseDown={() => startScrolling(-1)}
            onMouseUp={stopScrolling}
            onMouseLeave={stopScrolling}
            onTouchStart={() => startScrolling(-1)}
            onTouchEnd={stopScrolling}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
            aria-label="Scorri a destra"
            data-testid="carousel-arrow-right"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="flex justify-center mt-16">
          <CTAButton onClick={() => scrollToSection("accedi")} />
        </div>
      </div>
    </section>
  );
};

// Waitlist Form Section
const WaitlistSection = ({ onOpenTerms }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "",
    company: "",
    website: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const roles = ["Founder", "CEO", "COO", "CFO", "Operations Manager", "Altro"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.role || !formData.company) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Devi accettare i Termini e Condizioni");
      return;
    }

    if (!captchaVerified) {
      toast.error("Completa la verifica reCAPTCHA");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/waitlist`, formData);
      setSubmitted(true);
      toast.success("Registrazione completata!");
    } catch (error) {
      const message = error.response?.data?.detail || "Errore durante la registrazione";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="accedi"
        data-testid="section-waitlist"
        className="py-24 sm:py-32 lg:py-40 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-8">
              <Check className="w-10 h-10 text-neutral-700" />
            </div>
            <h2 
              className="text-3xl sm:text-4xl font-semibold tracking-tight text-black mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Grazie per esserti registrato!
            </h2>
            <p className="text-lg text-neutral-600">
              Ti contatteremo presto con le istruzioni per accedere alla BETA di IANUS.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="accedi"
      data-testid="section-waitlist"
      className="py-24 sm:py-32 lg:py-40 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-6">
              ACCEDI IN ANTEPRIMA
            </span>
            
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-black mb-8"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Partecipa alla rivoluzione
            </h2>
            
            <p className="text-lg text-neutral-600 leading-relaxed mb-8">
              I posti per accedere alla BETA di IANUS sono limitati. Registrati ora per richiedere 
              il tuo accesso prioritario in anteprima e trasforma il modo in cui gestisci i numeri della tua azienda.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-neutral-700" />
                </div>
                <span className="text-neutral-600">Accesso prioritario alla BETA della piattaforma</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-neutral-700" />
                </div>
                <span className="text-neutral-600">Supporto dedicato a partire dall'onboarding</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-neutral-700" />
                </div>
                <span className="text-neutral-600">Scontistiche e promozioni riservate agli "Early Adopter"</span>
              </div>
            </div>
          </div>

          <div className="bg-neutral-50 border border-black/5 rounded-3xl p-8 sm:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Nome e Cognome
                </label>
                <input
                  data-testid="input-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mario Rossi"
                  className="w-full bg-white border border-black/10 focus:border-black focus:ring-1 focus:ring-black rounded-xl px-4 py-3 text-base text-black transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Email aziendale
                </label>
                <input
                  data-testid="input-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mario@azienda.it"
                  className="w-full bg-white border border-black/10 focus:border-black focus:ring-1 focus:ring-black rounded-xl px-4 py-3 text-base text-black transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Ruolo
                </label>
                <select
                  data-testid="input-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full bg-white border border-black/10 focus:border-black focus:ring-1 focus:ring-black rounded-xl px-4 py-3 text-base text-black transition-all outline-none appearance-none cursor-pointer"
                >
                  <option value="">Seleziona il tuo ruolo</option>
                  {roles.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Nome Azienda
                </label>
                <input
                  data-testid="input-company"
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="La tua azienda S.r.l."
                  className="w-full bg-white border border-black/10 focus:border-black focus:ring-1 focus:ring-black rounded-xl px-4 py-3 text-base text-black transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Sito aziendale <span className="text-neutral-400 font-normal">(facoltativo)</span>
                </label>
                <input
                  data-testid="input-website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://www.tuaazienda.it"
                  className="w-full bg-white border border-black/10 focus:border-black focus:ring-1 focus:ring-black rounded-xl px-4 py-3 text-base text-black transition-all outline-none"
                />
              </div>

              {/* Terms and Conditions Checkbox */}
              <div className="flex items-start gap-3 mt-4">
                <input
                  data-testid="input-terms"
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-black/20 text-black focus:ring-black cursor-pointer"
                  required
                />
                <label htmlFor="terms" className="text-sm text-neutral-600 cursor-pointer">
                  Dichiaro di accettare{" "}
                  <button 
                    type="button"
                    onClick={onOpenTerms}
                    className="text-black underline hover:no-underline"
                  >
                    Termini e Condizioni
                  </button>
                </label>
              </div>

              {/* Simple CAPTCHA - Slider verification */}
              <div className="mt-4">
                <div 
                  className="relative bg-neutral-100 rounded-xl p-4 overflow-hidden"
                  style={{ userSelect: 'none' }}
                >
                  <div 
                    className={`absolute inset-0 bg-gradient-to-r from-cyan-400/30 to-cyan-500/30 transition-all duration-300 ${captchaVerified ? 'opacity-100' : 'opacity-0'}`}
                    style={{ width: captchaVerified ? '100%' : '0%' }}
                  />
                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 cursor-pointer ${captchaVerified ? 'bg-cyan-500 text-white' : 'bg-white border border-neutral-200 text-neutral-400 hover:border-neutral-300'}`}
                        onClick={() => setCaptchaVerified(!captchaVerified)}
                        data-testid="captcha-button"
                      >
                        {captchaVerified ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Shield className="w-5 h-5" />
                        )}
                      </div>
                      <span className={`text-sm transition-colors ${captchaVerified ? 'text-black font-medium' : 'text-neutral-600'}`}>
                        {captchaVerified ? 'Verifica completata' : 'Clicca per verificare'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-neutral-400">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="text-xs">Protezione anti-spam</span>
                    </div>
                  </div>
                </div>
              </div>

              <button
                data-testid="submit-button"
                type="submit"
                disabled={isSubmitting || !acceptedTerms || !captchaVerified}
                className="w-full bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-400 disabled:cursor-not-allowed rounded-xl px-8 py-4 font-semibold text-sm tracking-wide transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 cta-glow mt-8"
              >
                {isSubmitting ? (
                  "Registrazione in corso..."
                ) : (
                  <>
                    Richiedi accesso alla BETA
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="flex justify-center mt-16">
          <p className="text-sm text-neutral-500 text-center">
            Hai domande? Scrivici a{" "}
            <a href="mailto:info@ianusbudget.com" className="text-black underline hover:no-underline">
              info@ianusbudget.com
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

// Cookie Policy Modal
const CookiePolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold text-black" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Cookie Policy
          </h2>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh] text-sm text-neutral-600 space-y-4">
          <p className="font-medium text-black">Titolare del Trattamento dei Dati</p>
          <p>378 Gradi s.r.l - Via Settevalli 11 - 06128 Perugia (PG) - P. IVA 03540690546</p>
          
          <p className="font-medium text-black pt-4">Cosa sono i Cookie</p>
          <p>I cookie sono piccoli file di testo che i siti visitati inviano al browser dell'utente, dove vengono memorizzati, per poi essere ritrasmessi agli stessi siti alla visita successiva.</p>
          
          <p className="font-medium text-black pt-4">Tipologie di Cookie utilizzati</p>
          <p><strong>Cookie tecnici necessari:</strong> Sono essenziali per il corretto funzionamento del sito e non possono essere disabilitati. Includono cookie per la gestione della sessione e delle preferenze dell'utente (come il consenso ai cookie).</p>
          <p><strong>Cookie analitici:</strong> Utilizziamo questi cookie per raccogliere informazioni aggregate su come gli utenti utilizzano il nostro sito, al fine di migliorarne le funzionalità e l'esperienza utente.</p>
          
          <p className="font-medium text-black pt-4">Gestione dei Cookie</p>
          <p>Puoi gestire le preferenze sui cookie direttamente dal tuo browser. Tuttavia, disabilitare i cookie tecnici potrebbe compromettere alcune funzionalità del sito.</p>
          
          <p className="font-medium text-black pt-4">Durata dei Cookie</p>
          <p>I cookie tecnici hanno una durata limitata alla sessione di navigazione. I cookie di preferenza (come il consenso) vengono conservati per 12 mesi.</p>
          
          <p className="font-medium text-black pt-4">Diritti dell'utente</p>
          <p>In qualsiasi momento puoi esercitare i tuoi diritti previsti dal Regolamento UE 2016/679 (GDPR), inclusi il diritto di accesso, rettifica, cancellazione e opposizione al trattamento dei dati, contattando il Titolare all'indirizzo sopra indicato.</p>
          
          <p className="font-medium text-black pt-4">Aggiornamenti</p>
          <p>La presente Cookie Policy può essere soggetta ad aggiornamenti. Ti invitiamo a consultare periodicamente questa pagina.</p>
          
          <p className="text-xs text-neutral-400 pt-4">Ultimo aggiornamento: Aprile 2026</p>
        </div>
        <div className="p-6 border-t border-neutral-100">
          <button
            onClick={onClose}
            className="w-full bg-black text-white rounded-xl px-6 py-3 font-medium text-sm hover:bg-neutral-800 transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

// Terms and Conditions Modal
const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold text-black" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Termini e Condizioni
          </h2>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh] text-sm text-neutral-600 space-y-4">
          <p className="font-medium text-black">Titolare del Servizio</p>
          <p>378 Gradi s.r.l - Via Settevalli 11 - 06128 Perugia (PG) - P. IVA 03540690546</p>
          
          <p className="font-medium text-black pt-4">1. Oggetto del Servizio</p>
          <p>I presenti Termini e Condizioni regolano l'accesso e l'utilizzo del servizio IANUS, il Conversational Budget Manager sviluppato da 378 Gradi s.r.l. L'iscrizione alla lista d'attesa consente di ricevere informazioni sul lancio della piattaforma e di accedere in anteprima alla versione BETA.</p>
          
          <p className="font-medium text-black pt-4">2. Iscrizione alla Lista d'Attesa</p>
          <p>L'iscrizione alla lista d'attesa è gratuita e non comporta alcun obbligo di acquisto. I dati forniti (nome, email, ruolo, azienda) saranno utilizzati esclusivamente per le finalità indicate nell'informativa privacy.</p>
          
          <p className="font-medium text-black pt-4">3. Accesso alla BETA</p>
          <p>L'accesso alla versione BETA sarà concesso progressivamente agli iscritti alla lista d'attesa, a discrezione di 378 Gradi s.r.l. La versione BETA potrebbe presentare funzionalità limitate o bug, essendo una versione di test del servizio.</p>
          
          <p className="font-medium text-black pt-4">4. Proprietà Intellettuale</p>
          <p>Tutti i diritti di proprietà intellettuale relativi a IANUS, inclusi marchi, loghi, software e contenuti, sono di esclusiva proprietà di 378 Gradi s.r.l. È vietata qualsiasi riproduzione, distribuzione o utilizzo non autorizzato.</p>
          
          <p className="font-medium text-black pt-4">5. Limitazione di Responsabilità</p>
          <p>378 Gradi s.r.l. non sarà responsabile per eventuali danni diretti o indiretti derivanti dall'utilizzo della versione BETA del servizio. Il servizio viene fornito "così com'è" senza garanzie di alcun tipo.</p>
          
          <p className="font-medium text-black pt-4">6. Trattamento dei Dati Personali</p>
          <p>I dati personali saranno trattati in conformità al Regolamento UE 2016/679 (GDPR). Per maggiori informazioni, consultare l'informativa privacy disponibile sul sito.</p>
          
          <p className="font-medium text-black pt-4">7. Comunicazioni</p>
          <p>Iscrivendoti alla lista d'attesa, acconsenti a ricevere comunicazioni via email relative al servizio IANUS, inclusi aggiornamenti sullo sviluppo, inviti alla BETA e informazioni commerciali. Puoi disiscriverti in qualsiasi momento.</p>
          
          <p className="font-medium text-black pt-4">8. Modifiche ai Termini</p>
          <p>378 Gradi s.r.l. si riserva il diritto di modificare i presenti Termini e Condizioni in qualsiasi momento. Le modifiche saranno comunicate agli utenti iscritti via email.</p>
          
          <p className="font-medium text-black pt-4">9. Legge Applicabile e Foro Competente</p>
          <p>I presenti Termini e Condizioni sono regolati dalla legge italiana. Per qualsiasi controversia sarà competente il Foro di Perugia.</p>
          
          <p className="font-medium text-black pt-4">10. Contatti</p>
          <p>Per qualsiasi domanda o richiesta relativa ai presenti Termini e Condizioni, è possibile contattare 378 Gradi s.r.l. all'indirizzo email: info@ianusbudget.com</p>
          
          <p className="text-xs text-neutral-400 pt-4">Ultimo aggiornamento: Aprile 2026</p>
        </div>
        <div className="p-6 border-t border-neutral-100">
          <button
            onClick={onClose}
            className="w-full bg-black text-white rounded-xl px-6 py-3 font-medium text-sm hover:bg-neutral-800 transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

// Cookie Banner
const CookieBanner = ({ onOpenPolicy }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 bg-white border-t border-neutral-200 shadow-lg">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex-1 text-sm text-neutral-600 text-center sm:text-left">
          <p>
            Utilizziamo i cookie per migliorare la tua esperienza sul nostro sito. 
            Continuando la navigazione accetti l'utilizzo dei cookie.{" "}
            <button 
              onClick={onOpenPolicy}
              className="text-black underline hover:no-underline"
            >
              Leggi la Cookie Policy
            </button>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleReject}
            className="px-6 py-2.5 text-sm font-medium text-neutral-600 hover:text-black border border-neutral-300 hover:border-neutral-400 rounded-xl transition-colors"
            data-testid="cookie-reject"
          >
            Rifiuta
          </button>
          <button
            onClick={handleAccept}
            className="px-6 py-2.5 text-sm font-medium text-white bg-black hover:bg-neutral-800 rounded-xl transition-colors"
            data-testid="cookie-accept"
          >
            Accetta
          </button>
        </div>
      </div>
    </div>
  );
};

// Footer
const Footer = ({ onOpenCookiePolicy }) => (
  <footer className="py-8 bg-neutral-50 border-t border-black/5">
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <div className="flex flex-col items-center gap-4">
        <img 
          src="https://customer-assets.emergentagent.com/job_budget-ai-preview/artifacts/aema2oxx_Extended%20Color.png"
          alt="IANUS Logo"
          className="h-16 sm:h-20 w-auto bg-neutral-50"
        />
        <p className="text-xs text-neutral-500">
          © {new Date().getFullYear()} 378 Gradi s.r.l. Tutti i diritti riservati.
        </p>
        <p className="text-xs text-neutral-400 text-center">
          378 Gradi s.r.l - Via Settevalli 11 - 06128 Perugia (PG) - P. IVA 03540690546
        </p>
        <button 
          onClick={onOpenCookiePolicy}
          className="text-xs text-neutral-400 hover:text-neutral-600 underline hover:no-underline transition-colors"
        >
          Cookie Policy
        </button>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: id === "home" ? 0 : offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const openCookiePolicy = () => setShowCookiePolicy(true);
  const closeCookiePolicy = () => setShowCookiePolicy(false);
  
  const openTerms = () => setShowTerms(true);
  const closeTerms = () => setShowTerms(false);

  return (
    <div className="App min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Toaster position="top-center" richColors />
      <Header scrollToSection={scrollToSection} />
      <main>
        <HeroSection scrollToSection={scrollToSection} />
        <TargetSection scrollToSection={scrollToSection} />
        <ProblemSection scrollToSection={scrollToSection} />
        <DifferentiatorsSection scrollToSection={scrollToSection} />
        <WaitlistSection onOpenTerms={openTerms} />
      </main>
      <Footer onOpenCookiePolicy={openCookiePolicy} />
      <CookieBanner onOpenPolicy={openCookiePolicy} />
      <CookiePolicyModal isOpen={showCookiePolicy} onClose={closeCookiePolicy} />
      <TermsModal isOpen={showTerms} onClose={closeTerms} />
    </div>
  );
}

export default App;
